import * as React from "react"

import type {
  ConsoleRole,
  ConsoleUser,
  ConsoleUserId,
  ServiceKey,
  ServiceKeyId,
} from "./users-keys-data"

export interface CreateUserInput {
  email: string
  name: string | null
  role: ConsoleRole
}

export interface CreateKeyResult {
  key: ServiceKey
  plaintext: string
}

export interface UsersKeysStore {
  users: ConsoleUser[]
  keys: ServiceKey[]
  createUser: (input: CreateUserInput) => void
  changeRole: (userId: ConsoleUserId, role: ConsoleRole) => void
  removeUser: (userId: ConsoleUserId) => void
  createKey: (name: string) => CreateKeyResult
  revokeKey: (keyId: ServiceKeyId) => void
}

function randHex(bytes: number): string {
  let out = ""
  for (let i = 0; i < bytes; i++) {
    out += Math.floor(Math.random() * 256)
      .toString(16)
      .padStart(2, "0")
  }
  return out
}

export function useUsersKeysStore(
  seedUsers: ConsoleUser[],
  seedKeys: ServiceKey[],
): UsersKeysStore {
  const [users, setUsers] = React.useState<ConsoleUser[]>(seedUsers)
  const [keys, setKeys] = React.useState<ServiceKey[]>(seedKeys)

  // Rehydrate when the caller swaps the seed reference (real fetch resolves).
  const lastUsersSeed = React.useRef(seedUsers)
  const lastKeysSeed = React.useRef(seedKeys)
  React.useEffect(() => {
    if (lastUsersSeed.current !== seedUsers) {
      lastUsersSeed.current = seedUsers
      setUsers(seedUsers)
    }
  }, [seedUsers])
  React.useEffect(() => {
    if (lastKeysSeed.current !== seedKeys) {
      lastKeysSeed.current = seedKeys
      setKeys(seedKeys)
    }
  }, [seedKeys])

  const createUser = React.useCallback(({ email, name, role }: CreateUserInput) => {
    const now = new Date().toISOString()
    setUsers((prev) => [
      {
        id: `u-${Date.now()}` as ConsoleUserId,
        email,
        name,
        role,
        isBootstrap: false,
        createdAt: now,
        lastSeen: null,
        invitedAt: null,
        acceptedAt: null,
        isActive: true,
      },
      ...prev,
    ])
  }, [])

  const changeRole = React.useCallback((userId: ConsoleUserId, role: ConsoleRole) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)))
  }, [])

  const removeUser = React.useCallback((userId: ConsoleUserId) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId))
  }, [])

  const createKey = React.useCallback((name: string): CreateKeyResult => {
    const prefix = `pyx_${randHex(2)}`
    const secret = randHex(24)
    const plaintext = `${prefix}${secret}`
    const now = new Date().toISOString()
    const key: ServiceKey = {
      id: `k-${Date.now()}` as ServiceKeyId,
      name,
      prefix,
      createdAt: now,
      createdBy: "You",
      lastUsedAt: null,
      fromEnv: false,
    }
    setKeys((prev) => [key, ...prev])
    return { key, plaintext }
  }, [])

  const revokeKey = React.useCallback((keyId: ServiceKeyId) => {
    setKeys((prev) => prev.filter((k) => k.id !== keyId))
  }, [])

  return { users, keys, createUser, changeRole, removeUser, createKey, revokeKey }
}
